import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Role } from '@common/@types/auth';
import { Block, Credential, CredentialLookup } from '@common/@types/recommendations';
import LoadingScreen from '@common/components/LoadingScreen';
import Page from '@common/components/Page';
import StudentMultiselect from '@common/components/StudentMultiselect';
import RecommendationTable from '@common/components/learn/RecommendationTable';
import {
  getCurrentWeekStartEndDates,
  getStudentPeronsoalGoalMapBySubjects,
  getStudentWeeklyStatsGroupedBySubject,
} from '@common/components/timeback';
import useAuth from '@common/hooks/useAuth';
import useStudentSelection, { Student } from '@common/hooks/useStudentSelection';
import Layout from '@common/layouts';
import { setSelectedStudents } from '@common/redux/slices/dashboardFilterSelections';
import { trpc } from '@common/utils/trpcAmplify';
import { Alert, Box, Button, Modal, Stack, Typography, useTheme } from '@mui/material';
import HtmlRenderModal from 'common/components/HtmlRenderModal';
import VideoEmbedModal from 'common/components/VideoEmbedModal';
import LessonUnblockModal from 'common/components/learn/LessonUnblockModal';
import useStudentList from 'common/hooks/useStudentList';
import { PutCompetitionOnboardingStatusInput } from 'common/utils/amplify/putCompetitionOnboardingStatus';
import { switchRealtimeMetrics } from 'common/utils/realtimeActivityMetrics';
import NextGenOutOfHours from 'dash/src/sections/learn/NextGenOutOfHours';
import CompetitionOnboardingWizard from 'dash/src/sections/learn/competition/onboarding/CompetitionOnboardingWizard';
import InfoCard from 'dash/src/sections/learn/competition/onboarding/sections/infoCard';
import { MapWidget } from 'dash/src/sections/map/MapWidget';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

LearnPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout pageName="My Assigned Learning">{page}</Layout>;
};

type SlaInput = {
  studentId: string;
  level: string;
  v2: boolean;
};

interface StudentComplete {
  alpha_level: string;
  id: string;
  name: string;
  student_id: string;
  alpha_level_short: string;
  student_group: string;
  email?: string;
}

const unblockFilter: Record<string, string[]> = {
  '001': ['LL', 'WL'],
  '031': ['*'],
};

const statusesToIgnore = ['completed', 'invalidated', 'unblocked'];
export const UNBLOCK_OVERRIDE_STATUS = 'unblocked_override';

const checkUnblockOverride = (student: StudentComplete | undefined): boolean => {
  const campus_id = student?.student_id?.split('-')[0];
  const level = student?.alpha_level_short;
  if (!campus_id || !level) return false;
  return unblockFilter[campus_id]?.includes(level) || unblockFilter[campus_id]?.includes('*');
};

const unblockRecommendations = (recommendations: Block[] | undefined) =>
  recommendations?.map((recommendation) => ({
    ...recommendation,
    subBlocks: recommendation.subBlocks.map((subBlock) => ({
      ...subBlock,
      rows: subBlock.rows.map((row) => ({
        ...row,
        status: statusesToIgnore.includes(row.status) ? row.status : UNBLOCK_OVERRIDE_STATUS,
      })),
    })),
  }));

export default function LearnPage() {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  // Role info
  const { user } = useAuth();
  const userRole = user?.alphaAuthAttributes?.role;
  const email = user?.attributes.email;
  const name = user?.attributes['custom:customName'];

  // timestamp is used for refreshing the onboarding status
  const [competitionOnboardingTimestamp, setCompetitionOnboardingTimestamp] = useState<number>(
    Date.now()
  );
  const [showModal, setShowModal] = useState(false); // AI-GEN - Cursor - Claude3 - Opus
  const [khanIndexFetch, setKhanIndexFetch] = useState(0); // AI-GEN - Cursor - Claude3 - Opus
  const [mapIndexFetch, setMapIndexFetch] = useState(0); // AI-GEN - Cursor - Claude3 - Opus
  const [domain, setDomain] = useState(''); // AI-GEN - Cursor - Claude3 - Opus
  const [khanLink, setKhanLink] = useState(''); // AI-GEN - Cursor - Claude3 - Opus
  const [showIXLImageModal, setShowIXLImageModal] = useState(false);

  // Parse query params
  const { query } = router;
  const visibleRows = query.rows ? parseInt(query.rows as string) : 10;
  if (query.studentId) setSelectedStudents(query.studentId);
  const studentList = useStudentList(false);
  const selectedStudents = useStudentSelection();
  const student: Student = selectedStudents[0];
  const selectedStudent = useMemo(
    () => studentList?.find((std) => std.student_id === student?.id),
    [studentList, student]
  );

  console.log('selectedStudent', selectedStudent);
  const hasUnblockOverride = useMemo(
    () => checkUnblockOverride(selectedStudent),
    [selectedStudent]
  );

  const endpointInput: SlaInput | any = {};
  if (student && student.level) {
    endpointInput.studentId = student?.id;
    endpointInput.level = student?.level; // only used on getApps
    endpointInput.v2 = true; // only used on studentConfig
  }
  console.log('endpointInput', endpointInput);

  const {
    isLoading: isCompetitionOnboardingProgressLoading,
    fetchStatus: competitionOnboardingProgressFetchStatus,
    data: competitionOnboardingStatus,
  } = trpc.competitionOnboardingStatus.get.useQuery(
    { id: student?.id, timestamp: competitionOnboardingTimestamp },
    {
      snackbarErrors: true,
    }
  );

  const { fetchStatus: twoHourOnboardingProgressFetchStatus, data: twoHourOnboardingStatus } =
    trpc.twoHourOnboardingStatus.get.useQuery(
      { id: student?.id, khanIndexFetch },
      {
        snackbarErrors: true,
        enabled: !!student?.id,
      }
    );

  const { fetchStatus: mapStatusFetchStatus, data: mapStatus } = trpc.testing.map.get.useQuery(
    { student_id: student?.id?.split('-')[1], fetchIndex: mapIndexFetch },
    {
      snackbarErrors: true,
      enabled: !!student?.id,
    }
  );

  // Check for Screener tests with in_progress or pending status and redirect
  useEffect(() => {
    if (mapStatus && mapStatus.length > 0) {
      const hasActiveScreener = mapStatus.some(
        (entry: any) =>
          entry.type === 'Screener' &&
          !entry.completed_on && // Treat completed_on as the source of truth
          (entry.status === 'in_progress' || entry.status === 'pending')
      );

      if (hasActiveScreener) {
        router.push('/learn/map-test/');
      }
    }
  }, [mapStatus, router]);

  const saveOnboardingStatus = async (input: PutCompetitionOnboardingStatusInput) => {
    const mutation = trpc.competitionOnboardingStatus.put.useMutation();
    await mutation.mutate(input);
    setCompetitionOnboardingTimestamp(Date.now());
  };

  const saveKhanStatus = async (input: { student_id: string | null }) => {
    const mutation = trpc.competitionOnboardingStatus.putKhanStatus.useMutation();
    await mutation.mutate(input);
    setCompetitionOnboardingTimestamp(Date.now());
  };

  const saveOnboardingKhanStatus = async (input: { student_id: string | null }) => {
    if (!input.student_id) {
      enqueueSnackbar('Student ID not found', {
        variant: 'error',
      });
      return;
    } // AI-GEN - Cursor - Claude3 - Opus
    const mutation = trpc.twoHourOnboardingStatus.put.useMutation(); // AI-GEN - Cursor - Claude3 - Opus
    await mutation.mutate(input); // AI-GEN - Cursor - Claude3 - Opus
    setKhanIndexFetch((prevKhanIndexFetch) => prevKhanIndexFetch + 1); // AI-GEN - Cursor - Claude3 - Opus
  };

  const saveMapStatus = async (input: { student_id: string; subject: string }) => {
    if (!input.student_id) {
      enqueueSnackbar('Student ID not found', {
        variant: 'error',
      });
    }
    if (!input.subject) {
      enqueueSnackbar('Subject not found', {
        variant: 'error',
      });
      return;
    } // AI-GEN - Cursor - Claude3 - Opus
    const mutation = trpc.testing.map.put.useMutation(); // AI-GEN - Cursor - Claude3 - Opus
    try {
      await mutation.mutate(input); // AI-GEN - Cursor - Claude3 - Opus
      setMapIndexFetch((prevMapIndexFetch) => prevMapIndexFetch + 1); // AI-GEN - Cursor - Claude3 - Opus
      enqueueSnackbar('Map saved', { variant: 'success' }); // AI-GEN - Cursor - Claude3 - Opus
      window.location.reload(); // AI-GEN - Cursor - Claude3 - Opus
    } catch (error) {
      console.log(error);
      enqueueSnackbar(error, { variant: 'error' }); // AI-GEN - Cursor - Claude3 - Opus
    }
  };

  const { isLoading, data: studentLearningActivities } = trpc.studentLearningActivity.get.useQuery(
    endpointInput,
    { pollInterval: 10000, skipLoadingScreen: true, syncWithVisibility: true }
  );
  const recommendations = useMemo(
    () =>
      hasUnblockOverride
        ? unblockRecommendations(studentLearningActivities?.recommendations)
        : studentLearningActivities?.recommendations,
    [hasUnblockOverride, studentLearningActivities?.recommendations]
  );

  const { data: credentialLookupRaw } = trpc.apps.get.useQuery(endpointInput);

  const credentialLookup: CredentialLookup = {};
  credentialLookupRaw?.forEach((credential: Credential) => {
    credentialLookup[credential.app] = credential;
  });

  const apps = useMemo(() => Object.keys(credentialLookup), [credentialLookup]);

  const { data: videos } = trpc.apps.getHowToVideo.useQuery(
    { apps: apps },
    { enabled: Object.keys(credentialLookup).length > 0 }
  );

  const studentId = student?.id;
  const { startDate, endDate } = getCurrentWeekStartEndDates();

  /**
   * Fetch Weeks data for students activity
   */
  const { data: learningWeekData } = trpc.mergedTimeAndActivity.get.useQuery(
    {
      fromDate: startDate,
      toDate: endDate,
      studentIds: studentId ? [studentId] : [],
    },
    {
      mostRecentOnly: true,
      enabled: !!studentId,
    }
  );
  const todaysDate = dayjs().format('YYYY-MM-DD');

  const { data: realtimeDailyActivityData, fetchStatus: realtimeDailyActivityDataFetchStatus } =
    trpc.getStudentRealtimeActivity.get.useQuery(
      {
        startDate: todaysDate,
        endDate: todaysDate,
        students: studentId ? [studentId] : [],
        alphaLevel: student?.level ? [student?.level] : [],
      },
      {
        enabled: !!studentId && !!student?.level,
        pollInterval: 1000 * 60 * 2, // 2 minutes in milliseconds
      }
    );
  const isLoadingRealtime = useRef(true);
  if (realtimeDailyActivityDataFetchStatus === 'fetched') {
    isLoadingRealtime.current = false;
  }
  const hasFeedLoaded = !isLoadingRealtime.current && !isLoading;
  const { data: masteryThresholds } = trpc.masteryThresholds.get.useQuery(
    {
      studentIds: studentId ? [studentId] : [],
    },
    {
      enabled: !!studentId,
    }
  );
  const learningWeekDataLatest = useMemo(() => {
    if (!learningWeekData || !realtimeDailyActivityData) {
      return undefined;
    }
    const todaysData = learningWeekData.filter((item) => item.date === todaysDate);
    const otherDaysData = learningWeekData.filter((item) => item.date !== todaysDate);

    const todaysRealtimeData = switchRealtimeMetrics(realtimeDailyActivityData, todaysData);
    return [...otherDaysData, ...todaysRealtimeData];
  }, [learningWeekData, realtimeDailyActivityData]);

  /**
   * Fetch Subject Level Personal Goal
   */
  const { data: subjectsAndGoals } = trpc.goals.getStudentGoalByStudentId.useQuery(
    { studentId },
    { enabled: !!studentId }
  );

  const studentWeeklyStats = getStudentWeeklyStatsGroupedBySubject(learningWeekDataLatest, theme);
  const studentPersonalGoal = getStudentPeronsoalGoalMapBySubjects(subjectsAndGoals);

  useEffect(() => {
    if (credentialLookupRaw != null && credentialLookupRaw.length > 0) {
      // Send the credentials to the StudyReel extension so that it can autofill logins
      window.postMessage(
        { type: 'SetCredentials', credentials: credentialLookupRaw },
        window.location.origin
      );
    }
  }, [credentialLookupRaw]);

  useEffect(() => {
    // AI-GEN - Cursor - GPT4
    const shouldShowModal = // AI-GEN - Cursor - GPT4
      twoHourOnboardingProgressFetchStatus === 'fetched' && // AI-GEN - Cursor - GPT4
      competitionOnboardingProgressFetchStatus === 'fetched' && // AI-GEN - Cursor - GPT4
      (twoHourOnboardingStatus?.onboarding_status !== null || // AI-GEN - Cursor - GPT4
        competitionOnboardingStatus?.onboarding_status !== null) && // AI-GEN - Cursor - GPT4
      !twoHourOnboardingStatus?.khan_account_created && // AI-GEN - Cursor - GPT4
      !competitionOnboardingStatus?.khan_account_created; // AI-GEN - Cursor - GPT4
    if (shouldShowModal) {
      // AI-GEN - Cursor - GPT4
      const link = // AI-GEN - Cursor - GPT4
        twoHourOnboardingStatus?.khan_math_class_link || // AI-GEN - Cursor - GPT4
        competitionOnboardingStatus?.khan_math_class_link || // AI-GEN - Cursor - GPT4
        ''; // AI-GEN - Cursor - GPT4
      setKhanLink(link); // AI-GEN - Cursor - GPT4
      setShowModal(true); // AI-GEN - Cursor - GPT4
      if (twoHourOnboardingStatus?.onboarding_status !== null) {
        // AI-GEN - Cursor - GPT4
        setDomain('@2hourlearning.com'); // AI-GEN - Cursor - GPT4
      } else if (competitionOnboardingStatus?.onboarding_status !== null) {
        // AI-GEN - Cursor - GPT4
        setDomain('@esports.school'); // AI-GEN - Cursor - GPT4
      } // AI-GEN - Cursor - GPT4
    } else {
      // AI-GEN - Cursor - GPT4
      setShowModal(false); // AI-GEN - Cursor - GPT4
      setKhanLink(''); // AI-GEN - Cursor - GPT4
      setDomain(''); // AI-GEN - Cursor - GPT4
    } // AI-GEN - Cursor - GPT4
  }, [
    // AI-GEN - Cursor - GPT4
    twoHourOnboardingProgressFetchStatus, // AI-GEN - Cursor - GPT4
    twoHourOnboardingStatus, // AI-GEN - Cursor - GPT4
    competitionOnboardingProgressFetchStatus, // AI-GEN - Cursor - GPT4
    competitionOnboardingStatus, // AI-GEN - Cursor - GPT4
  ]); // AI-GEN - Cursor - GPT4

  // // Check if IXL banner should be shown
  // const shouldShowIXLBanner = useMemo(() => {
  //   const campus_id = student?.id?.split('-')[0];
  //   if (!campus_id) return false;
  //   const excludedCampuses = ['032', '034', '019', '018', '009', '036', '040', '033'];
  //   return !excludedCampuses.includes(campus_id);
  // }, [student?.id]);

  /**
   * Full list of states here:
   * - User is a student, student list is loading: show a single loading component
   * - User is a student, student list has loaded, rec list is loading: show the scaffold with small loading components
   * - User is a student, student list has loaded, rec list has loaded: show the rec list
   *
   * - User is not a student, no student selected: show the multiselect and "Please select..."
   * - User is not a student, student selected, rec list is loading: show the scaffold with small loading components
   * - User is not a student, student selected, rec list has loaded: show the rec list
   */
  const hasMultiselect = userRole !== Role.STUDENT;
  const loadingComponent = <LoadingScreen positionFixed={false} />;

  const handleCloseModal = async () => {
    // AI-GEN - Cursor - Claude3 - Opus
    setShowModal(false); // AI-GEN - Cursor - Claude3 - Opus
    if (domain === '@esports.school') {
      saveKhanStatus({ student_id: student?.id ?? null }); // AI-GEN - Cursor - Claude3 - Opus
    }
    if (domain === '@2hourlearning.com') {
      saveOnboardingKhanStatus({ student_id: student?.id ?? null }); // AI-GEN - Cursor - Claude3 - Opus
    }
  }; // AI-GEN - Cursor - Claude3 - Opus

  const handleOpenIXLImageModal = () => {
    setShowIXLImageModal(true);
  };

  const handleCloseIXLImageModal = () => {
    setShowIXLImageModal(false);
  };

  return (
    <Page title="Learn">
      <Box width={'100%'} display={'flex'} justifyContent={'center'} alignItems={'flex-start'}>
        <Stack marginX={'48px'} alignItems={'center'} spacing={2} width={'100%'}>
          {hasMultiselect && <StudentMultiselect enforceSingleSelect={true} />}

          {student ? (
            <Box width={'100%'}>
              <Box
                width={'100%'}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: '8px',
                  padding: '24px',
                  marginTop: '24px',
                }}
              >
                <Box
                  width={'100%'}
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  sx={{ marginBottom: '24px' }}
                >
                  <Typography variant={'h4'} color={theme.palette.text.secondary}>
                    My required learning
                  </Typography>
                </Box>

                {false && (
                  <Alert
                    severity="info"
                    sx={{
                      marginBottom: '24px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #1976d2',
                      '& .MuiAlert-message': {
                        width: '100%',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      Important Update: IXL Login Credentials Have Changed
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: '8px' }}>
                      Your IXL credentials have been updated. Please access your new login
                      credentials by clicking the 'key' icon next to the lesson link below.
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: '8px' }}>
                      If you are currently logged into your previous account, you may see an{' '}
                      <span
                        onClick={handleOpenIXLImageModal}
                        style={{
                          color: '#1976d2',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        error message
                      </span>{' '}
                      when you try to work on your lessons. Please log out, then log in again using
                      your new credentials by clicking on a lesson and entering the updated login
                      information.
                    </Typography>
                    <Typography variant="body2">
                      If you experience any difficulties accessing your account or if your new
                      credentials are not working, please contact our Support Team by clicking{' '}
                      <a
                        href="https://support.alpha.school"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'underline' }}
                      >
                        here
                      </a>{' '}
                      for immediate assistance.
                    </Typography>
                  </Alert>
                )}

                {false &&
                  competitionOnboardingProgressFetchStatus === 'fetched' &&
                  competitionOnboardingStatus?.onboarding_stage && (
                    <InfoCard
                      ageGrade={competitionOnboardingStatus?.original_age_grade ?? 0}
                      pickedGrade={competitionOnboardingStatus?.competition_grade ?? 0}
                      studentName={name ?? 'n/a'}
                    />
                  )}
                {false && mapStatusFetchStatus === 'fetched' && mapStatus?.length > 0 ? (
                  <Box mt={4}>
                    <MapWidget
                      mapDetails={{
                        studentId: student?.id || '',
                        name: name ?? 'n/a',
                        email: email || '',
                        age_grade: twoHourOnboardingStatus?.original_age_grade || 0,
                        map_data: mapStatus,
                        student: student,
                      }}
                      saveMapStatus={saveMapStatus}
                    />
                  </Box>
                ) : [undefined, 'pre_map_recommendations', 'recommendations'].includes(
                    competitionOnboardingStatus?.onboarding_stage
                  ) && competitionOnboardingProgressFetchStatus === 'fetched' ? (
                  <>
                    {!hasFeedLoaded ||
                    (['pre_map_recommendations', 'recommendations'].includes(
                      competitionOnboardingStatus?.onboarding_stage ?? ''
                    ) &&
                      competitionOnboardingProgressFetchStatus === 'fetched' &&
                      (studentLearningActivities?.recommendations?.length || 0) < 1) ? (
                      <>
                        <Typography
                          variant={'h4'}
                          sx={{
                            width: '100%',
                            backgroundColor: '#fff',
                            paddingTop: '40px',
                            paddingLeft: '10px',
                          }}
                        >
                          Generating your skill plan...
                        </Typography>
                        <LoadingScreen positionFixed={false} />
                      </>
                    ) : studentLearningActivities?.recommendations_title === 'NextgenOutOfHours' ? (
                      <NextGenOutOfHours />
                    ) : (
                      <RecommendationTable
                        masteryThresholds={masteryThresholds ?? []}
                        studentLearningActivities={recommendations}
                        credentialLookup={credentialLookup}
                        visibleRows={visibleRows}
                        videos={videos || {}}
                        studentWeeklyStats={studentWeeklyStats}
                        studentPersonalGoal={studentPersonalGoal}
                        full_student_id={studentId ?? ''}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {isCompetitionOnboardingProgressLoading ? (
                      <LoadingScreen positionFixed={false} />
                    ) : (
                      <CompetitionOnboardingWizard
                        competitionStatus={competitionOnboardingStatus}
                        student={student}
                        saveOnboardingStatus={saveOnboardingStatus}
                        MapDetails={{
                          studentId: competitionOnboardingStatus?.full_student_id || '', // Default to empty string if undefined
                          name: name || '', // Default to empty string if undefined
                          email: email || '', // Default to empty string if undefined
                          age_grade: competitionOnboardingStatus?.original_age_grade || 0, // Assuming false as a default, adjust based on actual logic needed
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          ) : hasMultiselect ? (
            <Typography variant={'h4'}>Please select a student.</Typography>
          ) : (
            loadingComponent
          )}
        </Stack>
      </Box>
      <Modal open={showModal} onClose={undefined}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: 'auto',
            maxWidth: '600px',
            display: 'flex', // AI-GEN - Cursor - Claude3 - Opus
            flexDirection: 'column', // AI-GEN - Cursor - Claude3 - Opus
            alignItems: 'center', // AI-GEN - Cursor - Claude3 - Opus
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
          >
            Setup your Khan account
          </Typography>
          <Typography component="div" sx={{ width: '100%', padding: '0 20px' }}>
            <ol>
              <li>
                Click{' '}
                <a href={khanLink} target="_blank" rel="noopener noreferrer">
                  this link
                </a>
              </li>
              <li>
                Click the blue button <strong>Create a new account</strong>
              </li>
              <li>Enter your birthdate</li>
              <li>
                Choose <strong>Continue with Google</strong>
              </li>
              <li>
                Choose your <strong>{domain}</strong> email
              </li>
              <li>
                Click <strong>Continue</strong>
              </li>
              <li>That's it! Click the button below and you're done!</li>
            </ol>
          </Typography>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{ mt: 2, alignSelf: 'center' }}
          >
            I'm Done, let's start learning!
          </Button>
        </Box>
      </Modal>

      {/* IXL Error Message Image Modal */}
      <Modal open={showIXLImageModal} onClose={handleCloseIXLImageModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
          >
            IXL Error Message Example
          </Typography>
          <Box
            component="img"
            src="/assets/illustrations/ixllogin.jpeg"
            alt="IXL Login Error Example"
            sx={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
          <Button onClick={handleCloseIXLImageModal} variant="contained" sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>

      <LessonUnblockModal />
      <VideoEmbedModal />
      <HtmlRenderModal />
    </Page>
  );
}
